import React, { useEffect, useState } from "react";
import { useNavigate, useParams, useLocation, Link } from "react-router-dom";
import { ArrowLeft } from "react-feather";
import toast from "react-hot-toast";
import { useGetLpoQuery, useCreateLpoMutation, useUpdateLpoMutation } from "../../stockApi";
import { useGetMaintenanceRequestQuery } from "../../../masters/vehicles/vehiclesApi";
import { useGetBranchesQuery } from "../../../admin/adminApi";
import { useClaims } from "../../../auth/useClaims";
import { useCanCreateLpo, useCanEditLpo } from "../../useStockPermissions";
import PartyPicker from "../../components/PartyPicker";
import LineItemsEditor, { emptyRow, computeTotals } from "../../components/LineItemsEditor";
import { paths } from "../../../../routes/paths";

const PURPOSE_TYPES = [
  { value: "GENERAL_REPLENISHMENT", label: "General Replenishment" },
  { value: "VEHICLE_MAINTENANCE", label: "Vehicle Maintenance" },
  { value: "CUSTOMER_SPECIFIC", label: "Customer Specific" },
  { value: "OTHER", label: "Other" },
];

const columns = { showRate: true, showVat: true, showRemarks: true, showReason: false, showMaxQty: false };

const toDateInput = (d) => (d ? new Date(d).toISOString().slice(0, 10) : "");

// LPO create/edit form. Two entry points:
// 1. Plain "New LPO" from the list - blank form.
// 2. "Convert to LPO" from PendingVehicleRequests.jsx - arrives via
//    location.state.fromVehicleRequest = {recordGuid, requestNumber}; this
//    form fetches that request's own detail (for its spare items and
//    branch) and prefills purposeType/vehicleRequestRecordGuid/lines. Still
//    fully editable before submit - this is a prefill, not an auto-submit.
const LpoForm = () => {
  const { recordGuid } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const isEditing = Boolean(recordGuid);
  const fromVehicleRequest = location.state?.fromVehicleRequest;

  const canCreate = useCanCreateLpo();
  const canEdit = useCanEditLpo();
  const { branches: callerBranchCodes, isAllBranches } = useClaims();
  const { data: branches = [] } = useGetBranchesQuery();
  const { data: lpo, isLoading: isLoadingLpo, isError: isLpoError, error: lpoError } = useGetLpoQuery(recordGuid, { skip: !isEditing });
  const { data: sourceRequest } = useGetMaintenanceRequestQuery(fromVehicleRequest?.recordGuid, {
    skip: !fromVehicleRequest?.recordGuid || isEditing,
  });
  const [createLpo, { isLoading: isCreating }] = useCreateLpoMutation();
  const [updateLpo, { isLoading: isUpdating }] = useUpdateLpoMutation();

  const availableBranches = isAllBranches
    ? branches
    : branches.filter((b) => callerBranchCodes?.includes(b.branchCode));

  const [branchId, setBranchId] = useState("");
  const [supplierOption, setSupplierOption] = useState(null);
  const [refNo, setRefNo] = useState("");
  const [lpoDate, setLpoDate] = useState(toDateInput(new Date()));
  const [purposeType, setPurposeType] = useState("GENERAL_REPLENISHMENT");
  const [purposeDescription, setPurposeDescription] = useState("");
  const [customerOption, setCustomerOption] = useState(null);
  const [vehicleRequest, setVehicleRequest] = useState(null); // {recordGuid, requestNumber}
  const [vatRate, setVatRate] = useState(0);
  const [discountAmount, setDiscountAmount] = useState(0);
  const [remarks, setRemarks] = useState("");
  const [changeSummary, setChangeSummary] = useState("");
  const [lines, setLines] = useState([emptyRow()]);
  const [seededFromRequest, setSeededFromRequest] = useState(false);

  // Prefill from an approved Vehicle Maintenance request's spare items.
  useEffect(() => {
    if (isEditing || !sourceRequest || seededFromRequest) return;
    setBranchId(sourceRequest.branchId);
    setPurposeType("VEHICLE_MAINTENANCE");
    setPurposeDescription(sourceRequest.serviceRepairDescription || "");
    setVehicleRequest({ recordGuid: sourceRequest.recordGuid, requestNumber: sourceRequest.requestNumber });
    if (sourceRequest.spareItems?.length) {
      setLines(
        sourceRequest.spareItems.map((item) => ({
          ...emptyRow(),
          productCode: item.productCode,
          productName: item.productName || "",
          unit: item.unit || "",
          qty: item.qty ?? "",
          rate: item.estimatedCost ?? "",
          locked: true,
        }))
      );
    }
    setSeededFromRequest(true);
  }, [isEditing, sourceRequest, seededFromRequest]);

  // Populate from an existing LPO when editing.
  useEffect(() => {
    if (!isEditing || !lpo) return;
    setBranchId(lpo.branchId);
    setSupplierOption(
      lpo.supplierName
        ? {
            value: lpo.supplierId ?? null,
            label: lpo.supplierName,
            customer: lpo.supplierId != null ? { custId: lpo.supplierId, branchId: lpo.supplierBranchId, custName: lpo.supplierName } : null,
          }
        : null
    );
    setRefNo(lpo.refNo || "");
    setLpoDate(toDateInput(lpo.lpoDate));
    setPurposeType(lpo.purposeType);
    setPurposeDescription(lpo.purposeDescription || "");
    setCustomerOption(
      lpo.customerId ? { value: lpo.customerId, label: lpo.customerName || String(lpo.customerId), customer: { custId: lpo.customerId, branchId: lpo.customerBranchId, custName: lpo.customerName } } : null
    );
    setVehicleRequest(lpo.vehicleRequestRecordGuid ? { recordGuid: lpo.vehicleRequestRecordGuid, requestNumber: lpo.vehicleRequestNumber } : null);
    setVatRate(lpo.vatRate || 0);
    setDiscountAmount(lpo.discountAmount || 0);
    setRemarks(lpo.remarks || "");
    setLines(
      lpo.lines?.length
        ? lpo.lines.map((l) => ({
            ...emptyRow(),
            productCode: l.productCode,
            productName: l.productName,
            unit: l.unit || "",
            qty: l.orderedQty,
            rate: l.unitRate,
            vatPercent: l.vatPercent,
            remarks: l.remarks || "",
          }))
        : [emptyRow()]
    );
  }, [isEditing, lpo]);

  const totals = computeTotals(lines, columns);
  const netAmount = totals.subTotal - (Number(discountAmount) || 0) + totals.vatAmount;
  const readOnly = isEditing ? !canEdit : !canCreate;

  const handleSave = async () => {
    if (!branchId) return toast.error("Branch is required");
    const supplierName = (supplierOption?.customer?.custName || supplierOption?.label || "").trim();
    if (!supplierName) return toast.error("Supplier / party name is required");
    const validLines = lines.filter((l) => l.productCode && Number(l.qty) > 0);
    if (validLines.length === 0) return toast.error("Add at least one line with a product and quantity");
    if (!purposeType) return toast.error("Purpose is required");
    if (!purposeDescription.trim()) return toast.error("Purpose Description is required");
    if (purposeType === "CUSTOMER_SPECIFIC" && !customerOption) return toast.error("Select a customer for a customer-specific LPO");
    if (isEditing && !changeSummary.trim()) return toast.error("Describe what changed before resubmitting");

    const body = {
      branchId: Number(branchId),
      supplierId: supplierOption?.customer?.custId ?? null,
      supplierBranchId: supplierOption?.customer?.branchId ?? null,
      supplierName,
      refNo: refNo.trim() || null,
      lpoDate: new Date(lpoDate).toISOString(),
      purposeType,
      purposeDescription: purposeDescription.trim(),
      customerId: purposeType === "CUSTOMER_SPECIFIC" ? customerOption?.customer?.custId ?? null : null,
      customerBranchId: purposeType === "CUSTOMER_SPECIFIC" ? customerOption?.customer?.branchId ?? null : null,
      vehicleRequestRecordGuid: vehicleRequest?.recordGuid ?? null,
      subTotal: totals.subTotal,
      vatRate: Number(vatRate) || 0,
      vatAmount: totals.vatAmount,
      discountAmount: Number(discountAmount) || 0,
      netAmount,
      remarks: remarks.trim() || null,
      lines: validLines.map((l) => ({
        productCode: Number(l.productCode),
        productName: l.productName,
        orderedQty: Number(l.qty),
        unit: l.unit || null,
        unitRate: Number(l.rate) || 0,
        vatPercent: Number(l.vatPercent) || 0,
        vatAmount: (Number(l.qty) || 0) * (Number(l.rate) || 0) * ((Number(l.vatPercent) || 0) / 100),
        lineTotal: (Number(l.qty) || 0) * (Number(l.rate) || 0) * (1 + (Number(l.vatPercent) || 0) / 100),
        remarks: l.remarks || null,
      })),
    };

    try {
      if (isEditing) {
        await updateLpo({ recordGuid, ...body, changeSummary: changeSummary.trim() }).unwrap();
        toast.success("LPO updated and resubmitted");
        navigate(paths.stockLpoDetail(recordGuid), { replace: true });
      } else {
        const created = await createLpo(body).unwrap();
        toast.success("LPO submitted");
        navigate(paths.stockLpoDetail(created.recordGuid), { replace: true });
      }
    } catch (error) {
      toast.error(error?.message || "Could not save the LPO");
    }
  };

  if (isEditing && isLpoError) {
    return (
      <div className="page-wrapper">
        <div className="content">
          <Link to={paths.stockLpos} className="d-inline-flex align-items-center mb-3">
            <ArrowLeft size={16} className="me-1" /> Back to LPOs
          </Link>
          <div className="alert alert-danger">Could not load this LPO ({lpoError?.message || "server error"}).</div>
        </div>
      </div>
    );
  }

  if (isEditing && isLoadingLpo) {
    return (
      <div className="page-wrapper">
        <div className="content"><p>Loading...</p></div>
      </div>
    );
  }

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="page-header">
          <div className="add-item d-flex">
            <div className="page-title">
              <Link to={paths.stockLpos} className="d-inline-flex align-items-center mb-2">
                <ArrowLeft size={16} className="me-1" /> Back to LPOs
              </Link>
              <h4>{isEditing ? `Edit LPO ${lpo?.lpoNumber || ""}` : "New LPO"}</h4>
            </div>
          </div>
        </div>

        {readOnly && (
          <div className="alert alert-info">
            You have read-only access. {isEditing ? "Editing" : "Creating"} LPOs requires the Stock.{isEditing ? "EditLpo" : "CreateLpo"} permission.
          </div>
        )}

        {vehicleRequest && (
          <div className="alert alert-success">
            Linked to Vehicle Maintenance Request <strong>{vehicleRequest.requestNumber}</strong>
          </div>
        )}

        <div className="card mb-4">
          <div className="card-body">
            <div className="row">
              <div className="col-md-3 mb-3">
                <label className="form-label">Branch</label>
                {isEditing ? (
                  <input type="text" className="form-control" value={lpo?.branchCode || ""} readOnly disabled />
                ) : (
                  <select className="form-control" value={branchId} onChange={(e) => setBranchId(e.target.value)} disabled={readOnly}>
                    <option value="">--Select--</option>
                    {availableBranches.map((b) => (
                      <option key={b.branchId} value={b.branchId}>{b.branchCode}</option>
                    ))}
                  </select>
                )}
              </div>
              <div className="col-md-4 mb-3">
                <label className="form-label">Supplier / Party (Customer Master)</label>
                <PartyPicker
                  mode="all-branches"
                  value={supplierOption}
                  onChange={setSupplierOption}
                  disabled={readOnly}
                  creatable
                />
              </div>
              <div className="col-md-2 mb-3">
                <label className="form-label">LPO Date</label>
                <input type="date" className="form-control" value={lpoDate} onChange={(e) => setLpoDate(e.target.value)} disabled={readOnly} />
              </div>
              <div className="col-md-3 mb-3">
                <label className="form-label">Reference No</label>
                <input type="text" className="form-control" value={refNo} onChange={(e) => setRefNo(e.target.value)} disabled={readOnly} />
              </div>
              <div className="col-md-3 mb-3">
                <label className="form-label">Purpose (required)</label>
                <select className="form-control" value={purposeType} onChange={(e) => setPurposeType(e.target.value)} disabled={readOnly || Boolean(vehicleRequest)}>
                  {PURPOSE_TYPES.map((p) => (
                    <option key={p.value} value={p.value}>{p.label}</option>
                  ))}
                </select>
              </div>
              <div className="col-md-6 mb-3">
                <label className="form-label">Purpose Description (required)</label>
                <input type="text" className="form-control" value={purposeDescription} onChange={(e) => setPurposeDescription(e.target.value)} disabled={readOnly} />
              </div>
              {purposeType === "CUSTOMER_SPECIFIC" && (
                <div className="col-md-6 mb-3">
                  <label className="form-label">For Customer</label>
                  <PartyPicker mode="all-branches" value={customerOption} onChange={setCustomerOption} disabled={readOnly} />
                </div>
              )}
            </div>

            <hr />
            <h5 className="mb-3">Line Items</h5>
            <LineItemsEditor
              branchId={branchId ? Number(branchId) : undefined}
              rows={lines}
              onChange={setLines}
              columns={columns}
              disabled={readOnly}
              defaultVatPercent={vatRate}
            />

            <div className="row mt-3">
              <div className="col-md-3 mb-3">
                <label className="form-label">Default VAT %</label>
                <input type="number" className="form-control" value={vatRate} onChange={(e) => setVatRate(e.target.value)} disabled={readOnly} />
              </div>
              <div className="col-md-3 mb-3">
                <label className="form-label">Discount Amount</label>
                <input type="number" className="form-control" value={discountAmount} onChange={(e) => setDiscountAmount(e.target.value)} disabled={readOnly} />
              </div>
              <div className="col-md-6 mb-3">
                <div className="d-flex justify-content-end gap-4">
                  <div>Sub Total: <strong>{totals.subTotal.toFixed(2)}</strong></div>
                  <div>VAT: <strong>{totals.vatAmount.toFixed(2)}</strong></div>
                  <div>Net Amount: <strong>{netAmount.toFixed(2)}</strong></div>
                </div>
              </div>
              <div className="col-md-8 mb-3">
                <label className="form-label">Remarks</label>
                <textarea className="form-control" rows={2} value={remarks} onChange={(e) => setRemarks(e.target.value)} disabled={readOnly} />
              </div>
              {isEditing && (
                <div className="col-md-4 mb-3">
                  <label className="form-label">What changed? (required to resubmit)</label>
                  <textarea className="form-control" rows={2} value={changeSummary} onChange={(e) => setChangeSummary(e.target.value)} disabled={readOnly} />
                </div>
              )}
            </div>

            {!readOnly && (
              <div className="d-flex gap-2">
                <button type="button" className="btn btn-primary" onClick={handleSave} disabled={isCreating || isUpdating}>
                  Save
                </button>
                <button type="button" className="btn btn-secondary" onClick={() => navigate(paths.stockLpos)}>
                  Cancel
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default LpoForm;
